import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-secondary">
        <Loader2 className="h-10 w-10 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-card">
      <div className="relative hidden lg:flex flex-col justify-between bg-primary p-12 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        <Logo light />
        <div className="relative">
          <ShieldCheck className="h-12 w-12 text-accent mb-6" />
          <h1 className="font-heading font-extrabold text-white text-4xl leading-tight">
            Owner Dashboard
          </h1>
          <p className="mt-4 text-slate-300 text-lg leading-relaxed max-w-md">
            Sign in to view and manage every booking request from your website — all in one place.
          </p>
        </div>
        <p className="relative text-slate-300 text-sm">Secure access · The Tech Savvy Company</p>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <div className="lg:hidden mb-10">
          <Logo />
        </div>
        <div className="max-w-sm w-full mx-auto">
          <h2 className="font-heading font-extrabold text-3xl text-foreground">Welcome back</h2>
          <p className="mt-3 text-muted-foreground text-lg">
            Sign in with your Google account to access your bookings.
          </p>

          <button
            data-testid="google-signin-button"
            onClick={signIn}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 h-14 rounded-full border-2 border-border bg-card text-foreground font-bold text-lg hover:border-accent hover:bg-secondary transition-colors"
          >
            <svg className="h-6 w-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
            </svg>
            Sign in with Google
          </button>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            Only authorized owner accounts can access the dashboard.
          </p>

          <Link
            to="/"
            className="mt-10 inline-flex items-center gap-2 text-accent font-bold hover:gap-3 transition-all"
          >
            <ArrowLeft className="h-5 w-5" /> Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
